const fontWeights = {
  thin: 100,
  hairline: 100,
  'ultra-light': 200,
  'extra-light': 200,
  light: 300,
  book: 300,
  regular: 400,
  normal: 400,
  plain: 400,
  roman: 400,
  standard: 400,
  medium: 500,
  'semi-bold': 600,
  'demi-bold': 600,
  bold: 700,
  'extra-bold': 800,
  'ultra-bold': 800,
  heavy: 900,
  black: 900,
  'heavy-black': 900,
  fat: 900,
  poster: 900,
  'ultra-black': 950,
  'extra-black': 950,
};
const sp = ' ';
const italicRE = /(italic|oblique)$/i;
const snakeCaseRE = /_/g;
const wordStartRE = /(^| )[a-z]/g;

/**
 * @param {string} font Font name, e.g. `'noto_sans_bold'` or `'Noto Sans Bold'`.
 * @return {string} Font name with words separated by spaces, e.g. `'Noto Sans Bold'`.
 */
function toWords(font) {
  if (font.indexOf('_') === -1) {
    return font;
  }
  // Snake case names, e.g. from VersaTiles styles, are all lower case.
  // Capitalized words match the family names that font services use.
  return font
    .replace(snakeCaseRE, sp)
    .replace(wordStartRE, (wordStart) => wordStart.toUpperCase());
}

const fontCache = {};

/**
 * @param {string|Array<string>} fonts Mapbox GL Style fontstack or single font, e.g. `['Open Sans Regular', 'Arial Unicode MS Regular']` or `'Open Sans Regular'`.
 * @param {number} size Font size in pixels.
 * @param {string|number} [lineHeight] Line height as css line-height.
 * @return {string} CSS font definition, e.g. `'normal 400 16px/1.2 "Open Sans"'`.
 */
export default function (fonts, size, lineHeight) {
  let cssData = fontCache[fonts];
  if (!cssData) {
    if (!Array.isArray(fonts)) {
      fonts = [fonts];
    }
    let weight = 400;
    let style = 'normal';
    const fontFamilies = [];
    let haveWeight, haveStyle;
    for (let i = 0, ii = fonts.length; i < ii; ++i) {
      const font = toWords(fonts[i]);
      const parts = font.split(' ');
      let maybeWeight = parts[parts.length - 1].toLowerCase();
      if (
        maybeWeight == 'normal' ||
        maybeWeight == 'italic' ||
        maybeWeight == 'oblique'
      ) {
        style = haveStyle ? style : maybeWeight;
        haveStyle = true;
        parts.pop();
        maybeWeight = parts[parts.length - 1].toLowerCase();
      } else if (italicRE.test(maybeWeight)) {
        maybeWeight = maybeWeight.replace(italicRE, '');
        style = haveStyle
          ? style
          : parts[parts.length - 1].replace(maybeWeight, '');
        haveStyle = true;
      }
      for (const w in fontWeights) {
        const previousPart =
          parts.length > 1 ? parts[parts.length - 2].toLowerCase() : '';
        if (
          maybeWeight == w ||
          maybeWeight == w.replace('-', '') ||
          previousPart + '-' + maybeWeight == w
        ) {
          weight = haveWeight ? weight : fontWeights[w];
          parts.pop();
          if (previousPart && w.startsWith(previousPart)) {
            parts.pop();
          }
          break;
        }
      }
      if (!haveWeight && typeof maybeWeight == 'number') {
        weight = maybeWeight;
        haveWeight = true;
      }
      let fontFamily = parts
        .join(sp)
        .replace('Klokantech Noto Sans', 'Noto Sans')
        .replace('DIN Pro', 'Barlow')
        .replace('Arial Unicode MS', 'Arial');
      if (fontFamily.indexOf(sp) !== -1) {
        fontFamily = '"' + fontFamily + '"';
      }
      fontFamilies.push(fontFamily);
    }
    // CSS font property: font-style font-weight font-size/line-height font-family
    cssData = [style, weight, fontFamilies];
    fontCache[fonts] = cssData;
  }
  return (
    cssData[0] +
    sp +
    cssData[1] +
    sp +
    size +
    'px' +
    (lineHeight ? '/' + lineHeight : '') +
    sp +
    cssData[2]
  );
}
